import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateStringRange,
  validationLength,
  validationType,
  validationMessages,
} from "../../actions/validations";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import {
  buildSnCCodeNameOptions,
  filterSnCBuildingOptions,
  sampleTestBlockMessage,
} from "../../../constants/sncSampleGuard";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { SnCPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  SNC_TOOLTIP_BY_SCREEN,
  sncTooltipGroupKey,
  type SnCStandardTooltipGroup,
} from "../../../constants/SnCTooltipInformation";
import { useTooltipText } from "../../../tooltips/TooltipContext";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

type RevenueTypeRow = {
  Building: string | null;
  "Item Code": string | null;
};

const emptyRevenueTypeRow = (): RevenueTypeRow => ({
  Building: null,
  "Item Code": null,
});

const CODE_FIELD = "Menu Item Code";
const NAME_FIELD = "Menu Item Name";

const mergeSelectedOptions = (
  options: string[],
  selected: string[],
): string[] => {
  const set = new Set(options);
  selected.forEach((value) => {
    if (value) {
      set.add(value);
    }
  });
  return Array.from(set);
};

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => Boolean(entry));
  }
  if (value == null || value === "") {
    return [];
  }
  return [String(value)];
};

const SnCMenuItems = ({
  data,
  fieldValues,
  buildingOptions = [],
}: {
  data: (value: any) => void;
  /** Full dashboard payload — includes Food Types, Units of Measure, and Retail Items */
  fieldValues: any;
  /** From GET /OnBoarding/GetBuildings — "code - name" labels */
  buildingOptions?: string[];
}) => {
  const sncTipGroup = sncTooltipGroupKey("Menu Items");
  const sncTipDefaults = SNC_TOOLTIP_BY_SCREEN["Menu Items"] as SnCStandardTooltipGroup;
  const tipHeader = useTooltipText(sncTipGroup, "header", sncTipDefaults.header);
  const tipCode = useTooltipText(sncTipGroup, "code", sncTipDefaults.code);
  const tipName = useTooltipText(sncTipGroup, "name", sncTipDefaults.name);
  const tipActive = useTooltipText(sncTipGroup, "active", sncTipDefaults.active);
  const safeBuildingOptions = filterSnCBuildingOptions(buildingOptions);
  const [menuItemCode, setMenuItemCode] = useState<string | null>(null);
  const [menuItemName, setMenuItemName] = useState<string | null>(null);
  const [foodTypes, setFoodTypes] = useState<string[]>([]);
  const [unitsOfMeasure, setUnitsOfMeasure] = useState<string[]>([]);
  const [active, setActive] = useState<boolean>(true);
  const [revenueTypes, setRevenueTypes] = useState<RevenueTypeRow[]>([
    emptyRevenueTypeRow(),
  ]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [codeFieldError, setCodeFieldError] = useState("");
  const [nameFieldError, setNameFieldError] = useState("");
  const [foodTypeError, setFoodTypeError] = useState(false);
  const [unitOfMeasureError, setUnitOfMeasureError] = useState(false);
  const [revenueTypeError, setRevenueTypeError] = useState(false);

  const foodTypeOptions = useMemo(
    () =>
      buildSnCCodeNameOptions(
        fieldValues?.[SnCPropertyEnum.foodTypes],
        "Food Type Code",
        "Food Type Name",
      ),
    [fieldValues?.[SnCPropertyEnum.foodTypes]],
  );

  const unitOfMeasureOptions = useMemo(
    () =>
      buildSnCCodeNameOptions(
        fieldValues?.[SnCPropertyEnum.unitsOfMeasure],
        "Unit of Measure Code",
        "Unit of Measure Name",
      ),
    [fieldValues?.[SnCPropertyEnum.unitsOfMeasure]],
  );

  const itemCodeOptions = useMemo(
    () =>
      buildSnCCodeNameOptions(
        fieldValues?.[SnCPropertyEnum.retailItems],
        "Item Number",
        "Item Description",
      ),
    [fieldValues?.[SnCPropertyEnum.retailItems]],
  );

  // Initialize once — do not re-sync from full payload or updatePayload loops and flickers.
  useEffect(() => {
    const menuItems = fieldValues?.[SnCPropertyEnum.menuItems];
    if (Array.isArray(menuItems)) {
      setItems(menuItems);
    }
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validateInput = (): boolean => {
    setCodeFieldError("");
    setNameFieldError("");
    setFoodTypeError(false);
    setUnitOfMeasureError(false);
    setRevenueTypeError(false);

    if (!menuItemCode) {
      setCodeFieldError(CODE_FIELD + validationMessages.sncMandatorySuffix);
    }
    if (!menuItemName) {
      setNameFieldError(NAME_FIELD + validationMessages.sncMandatorySuffix);
    }
    if (!foodTypes.length) {
      setFoodTypeError(true);
    }
    if (!unitsOfMeasure.length) {
      setUnitOfMeasureError(true);
    }

    // Revenue Type required once Building / Item Code options are wired.
    if (
      !menuItemCode ||
      !menuItemName ||
      !foodTypes.length ||
      !unitsOfMeasure.length
    ) {
      setAlertMessage(validationMessages.sncAllMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateStringRange(
        menuItemCode,
        validationLength.sncMenuItemCodeMin,
        validationLength.sncMenuItemCodeMax,
        validationType.CODE,
      )
    ) {
      setCodeFieldError(validationMessages.sncMenuItemCode);
      return false;
    }
    if (
      !validateStringRange(
        menuItemName,
        validationLength.sncMenuItemNameMin,
        validationLength.sncMenuItemNameMax,
        validationType.NAME,
      )
    ) {
      setNameFieldError(validationMessages.sncMenuItemName);
      return false;
    }
    const sampleBlock = sampleTestBlockMessage(
      menuItemCode,
      menuItemName,
      foodTypes,
      unitsOfMeasure,
      revenueTypes,
    );
    if (sampleBlock) {
      setAlertMessage(sampleBlock);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  const payload = {
    [CODE_FIELD]: menuItemCode,
    [NAME_FIELD]: menuItemName,
    "Food Types": foodTypes,
    "Units of Measure": unitsOfMeasure,
    Active: active ? "Yes" : "No",
    "Revenue Types": revenueTypes.filter(
      (row) => row.Building || row["Item Code"],
    ),
  };

  const updateItem = () => {
    if (!validateInput()) {
      return;
    }
    const otherItems = (items ?? []).filter(
      (item: any) => item.id !== selectedId,
    );
    if (checkDuplicate(otherItems, CODE_FIELD, menuItemCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SnCPropertyEnum.menuItems} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(items ?? [], CODE_FIELD, menuItemCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setCodeFieldError("");
      setNameFieldError("");
      setFoodTypeError(false);
      setUnitOfMeasureError(false);
      setRevenueTypeError(false);
      setMenuItemCode(list[CODE_FIELD] ?? null);
      setMenuItemName(list[NAME_FIELD] ?? null);
      setFoodTypes(
        toStringArray(list["Food Types"] ?? list["Food Type"]),
      );
      setUnitsOfMeasure(
        toStringArray(list["Units of Measure"] ?? list["Unit of Measure"]),
      );
      setActive(list.Active !== "No" && list.Active !== false);
      const savedRows = list["Revenue Types"];
      setRevenueTypes(
        Array.isArray(savedRows) && savedRows.length
          ? savedRows.map((row: any) => ({
              Building: row?.Building ?? null,
              "Item Code":
                row?.["Item Code"] ?? row?.["Revenue Item"] ?? null,
            }))
          : [emptyRevenueTypeRow()],
      );
    } else {
      setMenuItemCode(null);
      setMenuItemName(null);
      setFoodTypes([]);
      setUnitsOfMeasure([]);
      setActive(true);
      setRevenueTypes([emptyRevenueTypeRow()]);
      setCodeFieldError("");
      setNameFieldError("");
      setFoodTypeError(false);
      setUnitOfMeasureError(false);
      setRevenueTypeError(false);
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  const updateRevenueTypeRow = (
    index: number,
    field: keyof RevenueTypeRow,
    value: string | null,
  ) => {
    setRevenueTypes((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
    setRevenueTypeError(false);
  };

  const addRevenueTypeRow = () => {
    setRevenueTypes((prev) => [...prev, emptyRevenueTypeRow()]);
  };

  const removeRevenueTypeRow = (index: number) => {
    setRevenueTypes((prev) =>
      prev.length <= 1
        ? [emptyRevenueTypeRow()]
        : prev.filter((_, i) => i !== index),
    );
  };

  const setAllItemCodes = () => {
    const firstItemCode = revenueTypes[0]?.["Item Code"] ?? null;
    setRevenueTypes((prev) =>
      prev.map((row) => ({ ...row, "Item Code": firstItemCode })),
    );
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Menu Items
            <Tooltip
              title={tooltipComponent(tipHeader)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px">
          <Stack
            direction="row"
            className="form-two-col-row"
            flexWrap="wrap"
            gap="20px"
          >
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Menu Item Code *
                <Tooltip title={tooltipComponent(tipCode)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                required
                variant="outlined"
                size="small"
                placeholder="Menu Item Code"
                value={menuItemCode ?? ""}
                error={!!codeFieldError}
                helperText={codeFieldError}
                onChange={(e) => {
                  setMenuItemCode(e.target.value || null);
                  setCodeFieldError("");
                }}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Menu Item Name *
                <Tooltip title={tooltipComponent(tipName)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                required
                variant="outlined"
                size="small"
                placeholder="Menu Item Name"
                value={menuItemName ?? ""}
                error={!!nameFieldError}
                helperText={nameFieldError}
                onChange={(e) => {
                  setMenuItemName(e.target.value || null);
                  setNameFieldError("");
                }}
              />
            </Stack>
          </Stack>
          <Stack
            direction="row"
            className="form-two-col-row"
            flexWrap="wrap"
            gap="20px"
          >
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Food Type *
                <Tooltip title={tooltipComponent(tipName)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                multiple
                disablePortal
                size="small"
                options={mergeSelectedOptions(foodTypeOptions, foodTypes)}
                value={foodTypes}
                onChange={(_, v) => {
                  setFoodTypes(v);
                  setFoodTypeError(false);
                }}
                noOptionsText="Add Food Types first"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select food type(s)"
                    error={foodTypeError}
                    helperText={
                      foodTypeError ? validationMessages.fieldRequired : ""
                    }
                  />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Units of Measurement *
                <Tooltip title={tooltipComponent(tipName)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                multiple
                disablePortal
                size="small"
                options={mergeSelectedOptions(
                  unitOfMeasureOptions,
                  unitsOfMeasure,
                )}
                value={unitsOfMeasure}
                onChange={(_, v) => {
                  setUnitsOfMeasure(v);
                  setUnitOfMeasureError(false);
                }}
                noOptionsText="Add Units of Measure first"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select unit of measure(s)"
                    error={unitOfMeasureError}
                    helperText={
                      unitOfMeasureError
                        ? validationMessages.fieldRequired
                        : ""
                    }
                  />
                )}
              />
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" gap="2px">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active"
            />
            <Tooltip title={tooltipComponent(tipActive)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Stack>
          <Stack direction="column" gap="12px" width="100%">
            <Stack direction="row" alignItems="center" gap="4px">
              <FormLabel component="legend" error={revenueTypeError}>
                Revenue Type *
              </FormLabel>
              <Tooltip
                title={tooltipComponent(tipHeader)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            {revenueTypeError && (
              <Typography variant="caption" color="error">
                At least one Building and Item Code is required
              </Typography>
            )}
            {revenueTypes.map((row, index) => (
              <Stack
                key={`menu-revenue-type-row-${index}`}
                direction="row"
                gap="10px"
                alignItems="flex-start"
                className="form-two-col-row"
                flexWrap="wrap"
              >
                <Stack
                  direction="column"
                  className="form-two-col-field"
                  flex={1}
                >
                  <FormLabel>Building *</FormLabel>
                  <Autocomplete
                    size="small"
                    options={safeBuildingOptions}
                    value={row.Building}
                    onChange={(_, v) =>
                      updateRevenueTypeRow(index, "Building", v)
                    }
                    noOptionsText="Building options not configured yet"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select building"
                        error={revenueTypeError && !row.Building}
                      />
                    )}
                  />
                </Stack>
                <Stack
                  direction="column"
                  className="form-two-col-field"
                  flex={1}
                >
                  <FormLabel>Item Code *</FormLabel>
                  <Autocomplete
                    size="small"
                    options={itemCodeOptions}
                    value={row["Item Code"]}
                    onChange={(_, v) =>
                      updateRevenueTypeRow(index, "Item Code", v)
                    }
                    noOptionsText="No retail items in Retail Items Setup"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select item code"
                        error={revenueTypeError && !row["Item Code"]}
                      />
                    )}
                  />
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ pt: "28px" }}
                  gap="4px"
                >
                  {index === 0 ? (
                    <>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={addRevenueTypeRow}
                        aria-label="Add revenue type row"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={setAllItemCodes}
                      >
                        Set All
                      </Button>
                    </>
                  ) : (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => removeRevenueTypeRow(index)}
                      aria-label="Remove revenue type row"
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Menu Item
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Menu Items Available</Typography>
          {items?.length ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={CODE_FIELD}
              name={NAME_FIELD}
            />
          ) : (
            <Typography variant="body2">No Menu Items Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SnCMenuItems;
